import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { componentFieldsApi } from '../../../api/componentFieldsApi';
import { getCategoryField } from '../../../Helpers/componentTypeCategories';
import './ConfigValidation.css';
import { Building } from '../../../types/protocols';
import { BASE_TYPE_GROUPS } from '../../../Helpers/baseTypeUtils';
import { translate } from '../../../types/EnumTranslations';
import { getInvalidConfigTypes, getConfigValidationStats, InvalidConfigType } from '../MaintenanceDraft/utils/configValidation';
import { loadBuilding } from '../MaintenanceDraft/services/firebaseService';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import LifetimeEditModal from '../TemplatesEditor/LifetimeEditModal';
import { ComponentLifetime } from '../TemplatesEditor/types';
import { maintenanceTemplateApi } from '../../../api/maintenanceTemplateApi';
import { BaseComponentImpl } from '../../../types/implementations';
import ComponentDetailsView from '../../ComponentPage/ComponentDetailsView';
import { SpecificTypeModal } from '../MaintenanceDraft/components/modals';
import { SpecificTypeSelection } from '../MaintenanceDraft/components/modals/SpecificTypeModal';
import { applyComponentPatch, ComponentFieldPatch } from '../../../Helpers/componentPatch';
import ConfigTypeRow from './ConfigTypeRow';
import { BreadcrumbComponent } from '../../../Components/BreadCrumb';

// A group's types split by base type, so equal names in one group ("Målad betong"
// as golv and as vägg are two types) can be told apart; heading only when the
// group covers more than one base type
interface ConfigTypeSection {
    key: string;
    heading?: string;
    types: InvalidConfigType[];
}

const ConfigValidation: React.FC = () => {
    const { buildingKey } = useParams<{ buildingKey: string }>();
    const { types, getLifetime, getTranslation, getBaseType, getTypesForBaseType, refresh } = useBuildComponentTypes();

    const [building, setBuilding] = useState<Building | null>(null);

    // Set document title
    useDocumentTitle(`Validera konfiguration - ${building?.summary?.name || 'Byggnad'}`);
    const [loading, setLoading] = useState<boolean>(true);
    const [showLifetimeModal, setShowLifetimeModal] = useState<boolean>(false);
    const [selectedComponentType, setSelectedComponentType] = useState<string | null>(null);
    const [savingLifetime, setSavingLifetime] = useState<boolean>(false);
    const [updatedLifetimes, setUpdatedLifetimes] = useState<Record<string, ComponentLifetime>>({});
    const [selectedNewTypes, setSelectedNewTypes] = useState<Record<string, string>>({});
    const [savingComponent, setSavingComponent] = useState<string | null>(null);
    const [showSpecificTypeModal, setShowSpecificTypeModal] = useState<boolean>(false);
    const [selectedComponentForType, setSelectedComponentForType] = useState<BaseComponentImpl | null>(null);

    // The page keeps its own copy of the building, so edits saved in a
    // component's details are applied to it here
    const handleComponentPatched = (componentKey: string, patch: ComponentFieldPatch) => {
        setBuilding(prev => applyComponentPatch(prev, componentKey, patch));
    };

    // Load building data
    useEffect(() => {
        if (!buildingKey) return;

        const loadBuildingData = async () => {
            setLoading(true);
            try {
                const buildingData = await loadBuilding(buildingKey);
                if (buildingData) {
                    setBuilding(buildingData);
                } else {
                    console.error('Building not found');
                }
            } catch (error) {
                console.error('Error loading building:', error);
            } finally {
                setLoading(false);
            }
        };

        loadBuildingData();
    }, [buildingKey]);

    // Create a wrapper for getLifetime that checks local cache first
    const getLifetimeWithCache = React.useCallback(
        (key: string) => {
            return updatedLifetimes[key] || getLifetime(key);
        },
        [updatedLifetimes, getLifetime]
    );

    // Recalculate validation when building changes or local cache updates
    const invalidConfigTypes = React.useMemo(
        () => getInvalidConfigTypes(building, getLifetimeWithCache, getTranslation),
        [building, getLifetimeWithCache, getTranslation]
    );
    const stats = React.useMemo(
        () => getConfigValidationStats(building, getLifetimeWithCache),
        [building, getLifetimeWithCache]
    );
    const allValid = invalidConfigTypes.length === 0;

    // Group invalid config types by BaseType
    const groupedInvalidTypes = BASE_TYPE_GROUPS.map(group => {
        const typesInGroup = invalidConfigTypes.filter(({ componentType }) => {
            const baseType = getBaseType(componentType);
            return baseType && group.baseTypes.includes(baseType);
        });
        const sections: ConfigTypeSection[] = group.baseTypes.length > 1
            ? group.baseTypes
                .map(baseType => ({
                    key: baseType,
                    heading: translate(baseType),
                    types: typesInGroup.filter(({ componentType }) => getBaseType(componentType) === baseType)
                }))
                .filter(section => section.types.length > 0)
            : [{ key: group.key, types: typesInGroup }];
        return {
            ...group,
            types: typesInGroup,
            sections
        };
    }).filter(group => group.types.length > 0);

    // Find any invalid types that don't belong to any BASE_TYPE_GROUP
    const groupedTypeKeys = new Set(
        groupedInvalidTypes.flatMap(group => group.types.map(t => t.componentType))
    );
    const ungroupedTypes = invalidConfigTypes.filter(
        ({ componentType }) => !groupedTypeKeys.has(componentType)
    );

    // Separate orphaned types (no valid BaseType in config) from regular ungrouped types
    const orphanedTypes = ungroupedTypes.filter(({ componentType }) => {
        const baseType = getBaseType(componentType);
        return !baseType; // No baseType means it's orphaned (removed from config)
    });

    const regularUngroupedTypes = ungroupedTypes.filter(({ componentType }) => {
        const baseType = getBaseType(componentType);
        return baseType; // Has baseType but not in any group
    });

    // Add regular ungrouped types as a separate group if any exist
    const allGroupedTypes = regularUngroupedTypes.length > 0
        ? [
            ...groupedInvalidTypes,
            {
                key: 'ungrouped',
                label: 'Övriga komponenter (saknar kategori)',
                types: regularUngroupedTypes,
                sections: [{ key: 'ungrouped', types: regularUngroupedTypes } as ConfigTypeSection]
            }
        ]
        : groupedInvalidTypes;

    const handleOpenLifetimeModal = (componentType: string) => {
        setSelectedComponentType(componentType);
        setShowLifetimeModal(true);
    };

    const handleCloseLifetimeModal = () => {
        setShowLifetimeModal(false);
        setSelectedComponentType(null);
    };

    const handleSaveLifetime = async (lifetime: ComponentLifetime): Promise<void> => {
        if (!selectedComponentType) return;

        setSavingLifetime(true);
        try {
            await maintenanceTemplateApi.saveLifetime(selectedComponentType, lifetime, { source: 'draft', buildingKey });
            
            // Refresh configuration from database to get the latest values
            await refresh();
            
            // Update local cache with the new lifetime
            setUpdatedLifetimes(prev => ({
                ...prev,
                [selectedComponentType]: lifetime
            }));
            
            // Close modal
            handleCloseLifetimeModal();
        } catch (error) {
            console.error('Error saving lifetime:', error);
            alert('Misslyckades med att spara förväntad livslängd och kostnad');
        } finally {
            setSavingLifetime(false);
        }
    };

    const handleNewTypeChange = (componentKey: string, newType: string) => {
        setSelectedNewTypes(prev => ({
            ...prev,
            [componentKey]: newType
        }));
    };

    // Persist a type (and, for Utility/Outdoor, a category) via the API layer.
    // When no explicit category is given it follows the chosen type's category
    // in the same atomic write, so the two can't disagree.
    const persistTypeSelection = async (
        component: BaseComponentImpl,
        typeKey: string | null,
        category?: string | null
    ) => {
        if (!buildingKey) return;
        const categoryField = getCategoryField(component.type);
        const resolvedCategory = category ?? (typeKey ? types[typeKey]?.category : undefined);
        if (categoryField && resolvedCategory) {
            await componentFieldsApi.setTypeAndCategory(
                buildingKey,
                component.key,
                typeKey,
                resolvedCategory,
                categoryField,
                types
            );
        } else {
            await componentFieldsApi.updateField(buildingKey, component.key, 'specificType', typeKey);
        }
    };

    const handleSaveComponentType = async (componentKey: string, component: BaseComponentImpl) => {
        const newType = selectedNewTypes[componentKey];

        if (!newType) {
            alert('Välj en ny typ först');
            return;
        }

        if (!buildingKey) return;

        setSavingComponent(componentKey);
        try {
            await persistTypeSelection(component, newType);

            // Reload building data
            const buildingData = await loadBuilding(buildingKey);
            if (buildingData) {
                setBuilding(buildingData);
            }

            // Clear the selection
            setSelectedNewTypes(prev => {
                const newState = { ...prev };
                delete newState[componentKey];
                return newState;
            });
        } catch (error) {
            console.error('Error updating component type:', error);
            alert('Misslyckades med att uppdatera komponenttypen');
        } finally {
            setSavingComponent(null);
        }
    };

    const handleOpenSpecificTypeModal = (component: BaseComponentImpl) => {
        setSelectedComponentForType(component);
        setShowSpecificTypeModal(true);
    };

    const handleSaveSpecificType = async (selection: SpecificTypeSelection) => {
        if (!selectedComponentForType || !buildingKey || !building) return;

        try {
            await persistTypeSelection(selectedComponentForType, selection.typeKey, selection.category);

            // Reload building data
            const buildingData = await loadBuilding(buildingKey);
            if (buildingData) {
                setBuilding(buildingData);
            }
        } catch (error) {
            console.error('Error saving specificType:', error);
            throw error;
        }
    };

    if (!buildingKey) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnadsnyckel saknas</h4>
                <p>URL:en måste innehålla en byggnadsnyckel.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="loading-container">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <h5 className="text-muted">Validerar konfiguration...</h5>
                </div>
            </div>
        );
    }

    if (!building) {
        return (
            <div className="alert alert-warning">
                <h4>Byggnad hittades inte</h4>
                <p>Kunde inte ladda byggnadsinformation för nyckel: {buildingKey}</p>
            </div>
        );
    }

    const buildingName = building.summary?.name || 'Namnlös byggnad';

    return (
        <>
        <BreadcrumbComponent items={[{ text: buildingName, link: `/building/${buildingKey}` }, { text: 'Validera konfiguration' }]} />
        <div className="config-validation">
            {/* Action buttons */}
            <div className="action-buttons-top">
                <Link
                    to={`/building/${buildingKey}`}
                    className="btn btn-secondary"
                >
                    <ArrowLeft size={16} className="me-2" />
                    Tillbaka till byggnad
                </Link>
                {allValid && (
                    <Link
                        to={`/maintenanceDrafts/${buildingKey}`}
                        className="btn btn-success"
                    >
                        Fortsätt till underhållsplan
                        <ArrowRight size={16} className="ms-2" />
                    </Link>
                )}
            </div>

            {/* Header */}
            <div className="header-section">
                <div className="header-content" style={{ marginTop: '30px', marginBottom: '30px' }}>
                    <h1 className="page-title mb-2">
                        Validera konfiguration: {buildingName}
                    </h1>
                    <p className="lead text-muted mb-3">
                        Alla komponenttyper måste ha förväntad livslängd (positiva värden) och utbyteskostnad (större än 0 kr) angivna innan underhållsplan kan skapas
                    </p>
                    
                    {/* Progress stats */}
                    <div className="validation-stats">
                        <div className="stat-item">
                            <span className="stat-label">Totalt typer i byggnaden:</span>
                            <span className="stat-value">{stats.totalTypes}</span>
                        </div>
                        <div className="stat-item text-success">
                            <span className="stat-label">Kompletta:</span>
                            <span className="stat-value">{stats.validTypes}</span>
                        </div>
                        <div className="stat-item text-danger">
                            <span className="stat-label">Behöver åtgärdas:</span>
                            <span className="stat-value">{invalidConfigTypes.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success message */}
            {allValid && (
                <div className="alert alert-success mt-4">
                    <div className="d-flex align-items-center">
                        <CheckCircle size={24} className="me-3" />
                        <div>
                            <h5 className="mb-1">Alla typer har komplett konfiguration!</h5>
                            <p className="mb-0">
                                Du kan nu fortsätta till underhållsplanen och börja planera underhåll.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Invalid config types grouped by BaseType */}
            {!allValid && (
                <div className="main-content-section">
                    <div className="container-constrained">
                        {allGroupedTypes.map(group => (
                            <div key={group.key} className="config-group mb-4">
                                <div className="group-header">
                                    <h3>{group.label}</h3>
                                    <span className="badge bg-danger">
                                        {group.types.length} {group.types.length === 1 ? 'typ' : 'typer'}
                                    </span>
                                </div>
                                <div className="types-list">
                                    {group.sections.map(section => (
                                        <div key={section.key} className="config-type-section">
                                            {section.heading && (
                                                <h4 className="config-type-section-heading">{section.heading}</h4>
                                            )}
                                            {section.types.map(type => (
                                                <ConfigTypeRow
                                                    key={type.componentType}
                                                    type={type}
                                                    onEdit={handleOpenLifetimeModal}
                                                />
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Orphaned types - show all components in a single grid */}
                        {orphanedTypes.length > 0 && (() => {
                            // Flatten all orphaned components into a single array
                            const allOrphanedComponents = orphanedTypes.flatMap(({ components }) => components);
                            
                            return (
                                <div className="config-group mb-4">
                                    <div className="group-header">
                                        <h3>Komponenter med borttagna typer</h3>
                                        <span className="badge bg-warning">
                                            {allOrphanedComponents.length} {allOrphanedComponents.length === 1 ? 'komponent' : 'komponenter'}
                                        </span>
                                    </div>
                                    <div className="alert alert-warning mx-3 mt-3">
                                        <strong>Obs!</strong> Dessa komponenter har typer som har tagits bort från konfigurationen. 
                                        Du måste tilldela nya typer till dessa komponenter för att kunna fortsätta.
                                    </div>
                                    
                                    {/* Grid of all component cards */}
                                    <div className="orphaned-components-grid" style={{ padding: '20px' }}>
                                        {allOrphanedComponents.map(({ key, data }) => {
                                            const component = new BaseComponentImpl(data);
                                            const availableTypes = component.type ? getTypesForBaseType(component.type) : [];
                                            const selectedType = selectedNewTypes[key] || '';
                                            const isSaving = savingComponent === key;

                                            return (
                                                <div key={key} className="orphaned-component-card">
                                                    {/* Component Header */}
                                                    <div className="orphaned-component-card-header">
                                                        <h6 className="mb-1">
                                                            {component.label?.value || getTranslation(component.specificType?.value ?? undefined, component.type) || 'Namnlös komponent'}
                                                        </h6>
                                                        <div className="text-muted small mb-2">
                                                            ID: {key}
                                                        </div>
                                                    </div>

                                                    {/* Component Details */}
                                                    {building && buildingKey && (
                                                        <>
                                                            <div className="mb-3">
                                                                <ComponentDetailsView
                                                                    component={component}
                                                                    building={building}
                                                                    buildingKey={buildingKey}
                                                                    showHeader={false}
                                                                    onComponentPatched={handleComponentPatched}
                                                                    containerStyle={{ 
                                                                        border: '1px solid #dee2e6', 
                                                                        borderRadius: '6px', 
                                                                        padding: '12px',
                                                                        backgroundColor: '#f8f9fa',
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Type Selection */}
                                                            <div className="type-selection-card">
                                                                {component.specificType?.value && (
                                                                    <div className="text-muted mb-2 small">
                                                                        Borttagen typ: <strong>{component.specificType.value}</strong>
                                                                    </div>
                                                                )}
                                                                
                                                                {availableTypes.length > 0 ? (
                                                                    <>
                                                                        <label htmlFor={`type-select-${key}`} className="form-label small fw-semibold">
                                                                            Välj ny typ:
                                                                        </label>
                                                                        <select
                                                                            id={`type-select-${key}`}
                                                                            className="form-control form-control-sm mb-2"
                                                                            value={selectedType}
                                                                            onChange={(e) => handleNewTypeChange(key, e.target.value)}
                                                                            disabled={isSaving}
                                                                        >
                                                                            <option value="">-- Välj typ --</option>
                                                                            {availableTypes
                                                                                .sort((a, b) => getTranslation(a).localeCompare(getTranslation(b), 'sv'))
                                                                                .map((typeKey) => (
                                                                                    <option key={typeKey} value={typeKey}>
                                                                                        {getTranslation(typeKey)}
                                                                                    </option>
                                                                                ))}
                                                                        </select>
                                                                        <button
                                                                            className="btn btn-primary btn-sm w-100 mb-2"
                                                                            onClick={() => handleSaveComponentType(key, component)}
                                                                            disabled={!selectedType || isSaving}
                                                                        >
                                                                            {isSaving ? 'Sparar...' : 'Spara ny typ'}
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-outline-primary btn-sm w-100"
                                                                            onClick={() => handleOpenSpecificTypeModal(component)}
                                                                            disabled={isSaving}
                                                                        >
                                                                            Skapa ny typ
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div className="alert alert-warning alert-sm mb-2">
                                                                            <small>Inga tillgängliga typer för denna basetyp</small>
                                                                        </div>
                                                                        <button
                                                                            className="btn btn-primary btn-sm w-100"
                                                                            onClick={() => handleOpenSpecificTypeModal(component)}
                                                                            disabled={isSaving}
                                                                        >
                                                                            Skapa ny typ
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* Lifetime Edit Modal */}
            {showLifetimeModal && selectedComponentType && (
                <LifetimeEditModal
                    show={showLifetimeModal}
                    lifetime={getLifetimeWithCache(selectedComponentType)}
                    componentType={selectedComponentType as any}
                    onSave={handleSaveLifetime}
                    onCancel={handleCloseLifetimeModal}
                    saving={savingLifetime}
                />
            )}

            {/* Specific Type Modal */}
            {showSpecificTypeModal && selectedComponentForType && (
                <SpecificTypeModal
                    component={selectedComponentForType}
                    building={building}
                    onSave={handleSaveSpecificType}
                    onClose={() => {
                        setShowSpecificTypeModal(false);
                        setSelectedComponentForType(null);
                    }}
                />
            )}
        </div>
        </>
    );
};

export default ConfigValidation;
